/**
 * REUSABLE COMPONENT - Table
 * Generic table component with sorting and actions
 * Can be used in any React project
 */

import React, { useState } from 'react';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

const Table = ({
  columns,
  data,
  keyField = 'id',
  onSort,
  sortBy,
  sortOrder,
  striped = true,
  hoverable = true,
  bordered = false,
  emptyMessage = 'Aucune donnée disponible',
  loading = false,
  className = '',
}) => {
  const [localSortBy, setLocalSortBy] = useState(sortBy || null);
  const [localSortOrder, setLocalSortOrder] = useState(sortOrder || 'asc');

  const handleSort = (column) => {
    if (!column.sortable) return;

    const newSortOrder =
      localSortBy === column.key && localSortOrder === 'asc' ? 'desc' : 'asc';

    setLocalSortBy(column.key);
    setLocalSortOrder(newSortOrder);

    if (onSort) {
      onSort(column.key, newSortOrder);
    }
  };

  // Base classes
  const tableClasses = `min-w-full divide-y divide-gray-200 ${className}`;
  const containerClasses = `overflow-x-auto ${bordered ? 'border border-gray-200 rounded-lg' : ''}`;

  // Row classes
  const getRowClasses = (index) => {
    let classes = 'transition-colors';
    if (striped && index % 2 === 1) classes += ' bg-gray-50';
    if (hoverable) classes += ' hover:bg-gray-100';
    return classes;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      <table className={tableClasses}>
        <thead className="bg-gray-50">
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                  column.sortable ? 'cursor-pointer select-none hover:bg-gray-100' : ''
                } ${column.className || ''}`}
                onClick={() => handleSort(column)}
              >
                <div className="flex items-center gap-2">
                  <span>{column.label}</span>
                  {column.sortable && (
                    <div className="flex flex-col">
                      <ChevronUpIcon
                        className={`w-3 h-3 ${
                          localSortBy === column.key && localSortOrder === 'asc'
                            ? 'text-blue-600'
                            : 'text-gray-400'
                        }`}
                      />
                      <ChevronDownIcon
                        className={`w-3 h-3 -mt-1 ${
                          localSortBy === column.key && localSortOrder === 'desc'
                            ? 'text-blue-600'
                            : 'text-gray-400'
                        }`}
                      />
                    </div>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {data.map((row, rowIndex) => (
            <tr key={row[keyField] || rowIndex} className={getRowClasses(rowIndex)}>
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    column.cellClassName || ''
                  }`}
                >
                  {column.render
                    ? column.render(row[column.key], row, rowIndex)
                    : row[column.key] || '-'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default Table;
